import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { ArrowLeft } from 'lucide-react';

import defaultCompanyLogo from '../../../assets/images/CompanyDefaultLogo.svg';
import defaultProfileImg from '../../../assets/images/default_profile_img.svg';
import loginIcon from '../../../assets/images/icon-login-2.svg';
import bellIcon from '../../../assets/images/tabler-icon-bell.svg';
import { IsMobile } from '../../../helpers/IsMobile.js';
import urls from '../../../routing/urls.js';
import { selectCurrentUser } from '../../../selectors/authSelectors';
import { selectOpeningDetails, selectOpeningListLoading } from '../../../selectors/browseSelectors';
import { selectCompanyProfile } from '../../../selectors/companyProfileSelectors.js';
import { selectUnreadNotifications } from '../../../selectors/notificationSelectors.js';
import { selectCurrentOrganization } from '../../../selectors/organizationSelectors';
import baseServerUrl from '../../../services/apiServer';
import DropDown from '../../SideMenu/DropDown/DropDown.jsx';
import BrowseNotification from '../BrowseNotification/BrowseNotification';
import BrowseNotificationDropDown from '../BrowseNotification/BrowseNotificationDropDown/BrowseNotificationDropDown.jsx';
import ButtonWithIcon from '../ButtonWithIcon/ButtonWithIcon.jsx';
import ProfileImage from '../ProfileImage/ProfileImage';
import SendReferralButton from '../SendReferralButton/SendReferralButton';
import ProfileIconLoader from '../SkeletonLoaders/ProfileIconLoader/ProfileIconLoader.jsx';

import styles from './Header.module.scss';

const Header = ({ title, isPublic, backButton, backUrl = -1, logo, logoClassName }) => {
  const { t } = useTranslation('auth');
  const mobile = IsMobile();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentUser = useSelector(selectCurrentUser);
  const company = useSelector(selectCompanyProfile);
  const openingDetails = useSelector(selectOpeningDetails);
  const unreadCountNotification = useSelector(selectUnreadNotifications);
  const selectedOrganization = useSelector(selectCurrentOrganization);
  const openingListLoading = useSelector(selectOpeningListLoading);

  const [unreadCount, setUnreadCount] = useState(unreadCountNotification);

  const [profileImage, setProfileImage] = useState(defaultProfileImg);

  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [navigationHeaderTitle, setNavigationHeaderTitle] = useState(title);
  const [navigationHeaderLogo, setNavigationHeaderLogo] = useState(logo || defaultCompanyLogo);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (currentUser) {
      setProfileImage(currentUser?.avatar_thumb || defaultProfileImg);
      setUnreadCount(unreadCountNotification);
    }
  }, [currentUser, unreadCountNotification, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    if (isPublic) {
      if (!selectedOrganization && isPublic) {
        setNavigationHeaderTitle(openingDetails?.organization_name);
        setNavigationHeaderLogo(openingDetails?.logo_image_url || logo || defaultCompanyLogo);
      } else if (company) {
        setNavigationHeaderTitle(company?.name);
        setNavigationHeaderLogo(company?.logo_image?.thumb_url || defaultCompanyLogo);
      } else if (selectedOrganization) {
        setNavigationHeaderTitle(selectedOrganization?.name);
        setNavigationHeaderLogo(selectedOrganization?.logo_image_thumb || defaultCompanyLogo);
      }
    }
  }, [isPublic, company, openingDetails, setNavigationHeaderTitle, setNavigationHeaderLogo, selectedOrganization]);

  const handleBellClick = () => {
    setDropdownVisible(!isDropdownVisible);
  };

  const sendReferralUrl = useMemo(
    () =>
      selectedOrganization?.general_setting_identifier
        ? urls.companyReferral.replace(':companyName', selectedOrganization?.general_setting_identifier)
        : urls.refer,
    [selectedOrganization, urls]
  );

  return (
    <div className={styles.mainHeader}>
      <div className={styles.titleHeading}>
        {isPublic && openingListLoading && <ProfileIconLoader />}
        {isPublic && !openingListLoading && (
          <>
            <div>
              <img
                src={navigationHeaderLogo}
                alt="logo"
                className={classNames(styles.navigationHeaderLogo, { [logoClassName]: navigationHeaderLogo === logo })}
              />
            </div>
            {!mobile && <div className={styles.navigationHeaderTitlePublic}>{navigationHeaderTitle}</div>}
          </>
        )}
        {!isPublic && !mobile && (
          <div>
            <p className={styles.navigationHeaderTitle}>{navigationHeaderTitle}</p>
            {backButton && (
              <button
                type="button"
                onClick={() => navigate(backUrl)}
                className="flex items-center gap-1 p-0 mt-1 text-xs font-medium text-gray-500 bg-transparent border-none"
              >
                <ArrowLeft /> {backButton}
              </button>
            )}
          </div>
        )}
      </div>

      {!isPublic && title !== 'My Account' && title !== 'Payments Portal' && (
        <div className={styles.mobileDropDown}>
          <DropDown />
        </div>
      )}
      <div className={styles.browseDisplay}>
        {currentUser ? (
          <>
            <div>
              <SendReferralButton instantReferralLink={sendReferralUrl} />
            </div>
            <div className={styles.notificationContainer} ref={dropdownRef}>
              <div
                onClick={handleBellClick}
                className={styles.bellIconDiv}
                role="button"
                tabIndex={0}
                onKeyPress={handleBellClick}
              >
                {unreadCount !== 0 && (
                  <div className={styles.notificationCount}>
                    <span className={styles.notificationText}>{unreadCount > 99 ? '99+' : unreadCount}</span>
                  </div>
                )}
                <BrowseNotification img={bellIcon} isVisible={isDropdownVisible} />
              </div>
              {isDropdownVisible && (
                <BrowseNotificationDropDown isVisible={isDropdownVisible} setUnreadCount={setUnreadCount} />
              )}
            </div>

            {!mobile && (
              <div>
                <ProfileImage img={profileImage} />
              </div>
            )}
          </>
        ) : (
          <div className={styles.btnRow}>
            <ButtonWithIcon
              text={t('common:signUpLabel')}
              textClassName="btn-icon-text-tertiary-40"
              variant="tertiary-36"
              onClick={() => {
                window.location.href = `${baseServerUrl}/signup`;
              }}
            />
            <ButtonWithIcon
              img={loginIcon}
              text={t('common:login')}
              imgClassName="btn-icon-36"
              textClassName="btn-icon-text-tertiary-40"
              variant="tertiary-36"
              onClick={() => {
                window.location.href = `${baseServerUrl}/login`;
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;
